import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { Card, DonutChart, Legend, Text } from "@tremor/react";
import { ConversationsSummaryByTagInPeriodData } from "@hilos/types/analytics";
import Loading from "src/components/Loading";
import LocalTable from "src/components/LocalTable";
import Tooltip from "src/components/Tooltip";
import {
  useInboxUsageSummaryAnalytics,
  useInboxUsageSummaryConversations,
} from "src/hooks/useInboxUsageSummaryByTagAnalytics";
import { API_ROUTES } from "src/router/router";
import { paramsToQueryString } from "../FiltersMeta";
import InboxUsageDownloadCSVButton from "../InboxUsageDownloadCSVButton";
import { InboxReportProps } from "../summary/InboxSummary";

export default function InboxByTag({ params }: InboxReportProps) {
  const { t } = useTranslation();
  const paramsQS = paramsToQueryString(params);
  const { inboxUsageSummary, columnsTable: inboxSummaryColumnsTable } =
    useInboxUsageSummaryAnalytics(paramsQS + "&period_type_type=WEEK");
  const { inboxUsageSummaryConversations } =
    useInboxUsageSummaryConversations(paramsQS);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <InboxUsageDownloadCSVButton
          url={API_ROUTES.ANALYTICS_INBOX_USAGE_TAG_SUMMARY}
          params={paramsQS + "&period_type_type=WEEK"}
          report_type="Inbox Summary By Tag"
        />
      </div>
      <div className="grid grid-cols-3 gap-x-6">
        <div className="space-y-6">
          {inboxUsageSummaryConversations ? (
            <Card className="max-w-lg px-4 py-3 sm:px-6 sm:py-4">
              <Text className="flex items-center">
                {t(
                  "reports:by-tag.conversations-by-tag",
                  "Conversations by Tag"
                )}
                <Tooltip
                  content={
                    <div className="min-w-[170px] px-2 py-3 text-sm text-gray-500 sm:p-3 ">
                      {t(
                        "reports:by-tag.conversations-by-tag-tooltip",
                        "If a conversation has multiple tags, it will be counted once per each tag"
                      )}
                    </div>
                  }
                />
              </Text>
              <Text></Text>
              <DonutChart
                noDataText={t("reports:summary.no-data", "No data")}
                className="mt-6"
                data={
                  inboxUsageSummaryConversations.period &&
                  inboxUsageSummaryConversations.period.conversations_per_tag.map(
                    (data) => ({
                      source: data[0],
                      count: data[1],
                    })
                  )
                }
                category="count"
                index="source"
                showLabel
                colors={["slate", "violet", "indigo", "rose", "cyan", "amber"]}
              />
              <Legend
                className="mt-3"
                categories={inboxUsageSummaryConversations.period.conversations_per_tag.map(
                  (data) => data[0]
                )}
                colors={["slate", "violet", "indigo", "rose", "cyan", "amber"]}
              />
            </Card>
          ) : (
            <Loading />
          )}
        </div>
        <div className="col-span-2">
          {inboxUsageSummary && inboxUsageSummary.period ? (
            <div>
              <LocalTable<ConversationsSummaryByTagInPeriodData>
                tableTitle={
                  <>
                    {t(
                      "reports:by-tag.summary-per-tag",
                      "Conversations summary (per tag)"
                    )}
                  </>
                }
                columns={
                  inboxSummaryColumnsTable as unknown as ColumnDef<ConversationsSummaryByTagInPeriodData>[]
                }
                data={inboxUsageSummary.period.conversations_summary_in_period}
                columnVisibility={{
                  should_be_included: false,
                }}
                enableColumnSorting={true}
                disableSearch={true}
              />
            </div>
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </div>
  );
}
